/**
 * Emits the free-tier upsell funnel's analytics events.
 *
 * Why a module of its own rather than two more entries in useOnboardingEvents:
 * every event there is `onboarding_`-prefixed and scoped to the signup wizard.
 * These two fire long after onboarding, from the steady-state dashboard, so
 * mixing them would make the onboarding namespace mean two different things.
 * The transport itself is NOT duplicated — both modules call the shared
 * analyticsTransport.emit.
 *
 * Why these events exist at all: the free-tier card used to navigate to
 * /dashboard/account#upgrade-options, and that page view was the only
 * observable signal that anyone tapped it. Opening the plans sheet in place
 * deletes the navigation, so the signal has to be emitted explicitly or the
 * funnel goes dark.
 *
 * The two are deliberately not one event. `upsell_pill_tap` is intent and is
 * fired by the card itself, so it records the tap even if a host ignored the
 * emit or was mid-teardown; `upsell_sheet_open` is the outcome and is fired by
 * the host that actually mounted the dialog. A gap between the two counts is a
 * real defect, which is only visible because they are separate.
 */
import { emit } from "@/composables/analyticsTransport.js";

const UPSELL_EVENT_NAMES = Object.freeze({
  PILL_TAP: "upsell_pill_tap",
  SHEET_OPEN: "upsell_sheet_open",
});

// Which surface the user was standing on: the two hosts of the free-tier card.
// Lower-cased like every other enum-ish gtag param in the repo, and defaulted
// to "" rather than undefined because gtag drops undefined params silently,
// which reads in GA4 as "dimension absent" instead of "dimension wrong".
const normalizeSource = (source) => (source || "").toLowerCase();

export function useUpsellEvents() {
  return {
    // The free-tier usage card was tapped.
    trackPillTap(payload) {
      emit(UPSELL_EVENT_NAMES.PILL_TAP, {
        source: normalizeSource(payload?.source),
      });
    },

    // The plans sheet was actually opened in response.
    trackSheetOpen(payload) {
      emit(UPSELL_EVENT_NAMES.SHEET_OPEN, {
        source: normalizeSource(payload?.source),
      });
    },
  };
}

export { UPSELL_EVENT_NAMES };
